import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
    Company,
    StorageConstants,
    WorkflowOrigin,
    WorkflowStep,
    WorkflowStepType,
    WorkflowType,
} from '@warema/shared/models';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { WorkflowDataInitializationService } from './workflow-data-initialization.service';
import * as WorkflowActions from '../state/workflow.actions';
import { WorkflowState } from '../state/workflow.reducers';
import * as WorkflowSelectors from '../state/workflow.selectors';

@Injectable({
    providedIn: 'root',
})
export class WorkflowService {
    private readonly _route = inject(ActivatedRoute);
    private readonly _router = inject(Router);
    private readonly _store = inject<Store<WorkflowState>>(Store);
    private readonly _workflowDataInitializationService = inject(WorkflowDataInitializationService);

    public steps$: Observable<Array<WorkflowStep>>;
    public activatedStep$: Observable<WorkflowStep | undefined>;
    public previouslyActivatedStep$: Observable<WorkflowStep | undefined>;
    public workflowOrigin$ = new BehaviorSubject<WorkflowOrigin | null>(null);
    public workflowType$ = new BehaviorSubject<WorkflowType | null>(null);

    public readonly digitalBuyingAdvisorSelectionPage = signal<Company | null>(null);

    constructor() {
        this.steps$ = this._store.select(WorkflowSelectors.selectAllSteps);
        this.activatedStep$ = this._store.select(WorkflowSelectors.selectActivatedStep);
        this.previouslyActivatedStep$ = this._store.select(WorkflowSelectors.selectPreviouslyActivatedStep);
        // eslint-disable-next-line @ngrx/no-store-subscription
        this._store.select(WorkflowSelectors.selectWorkflowOrigin).subscribe(this.workflowOrigin$);
        // eslint-disable-next-line @ngrx/no-store-subscription
        this._store.select(WorkflowSelectors.selectWorkflowType).subscribe(this.workflowType$);

        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                switchMap((event: NavigationEnd) => this.steps$.pipe(map(steps => ({ event, steps })))),
                takeUntilDestroyed(),
            )
            .subscribe(({ event, steps }) => {
                const newStep = steps.find(step => event.urlAfterRedirects.includes(step.url));
                if (newStep) {
                    this._store.dispatch(WorkflowActions.SetStep({ newStep }));
                }
            });
    }

    public async changeStep(stepType: WorkflowStepType): Promise<void> {
        const steps = await lastValueFrom(this.steps$.pipe(first()));
        const nextStepIndex = steps.findIndex(x => x.workflowStepType === stepType);
        if (nextStepIndex === -1) {
            throw new Error('Error while changing step: step not found in the workflow steps');
        }

        const activatedStep = await lastValueFrom(this.activatedStep$.pipe(first()));
        const currentStepIndex = steps.indexOf(activatedStep);
        const stepsToValidate = steps.slice(currentStepIndex === -1 ? 0 : currentStepIndex, nextStepIndex);
        if (stepsToValidate.some(x => !x.isValid)) {
            console.warn('Unable to change step, some prior steps are not valid!');
            // TODO B2C-5236: handle this case - e.g. navigate to the first invalid step
            return;
        }

        await this._router.navigate([steps[nextStepIndex].url], {
            queryParamsHandling: 'merge',
            relativeTo: this._route,
        });
    }

    public async goToNextStep(): Promise<void> {
        const steps = await lastValueFrom(this.steps$.pipe(first()));
        const activatedStep = await lastValueFrom(this.activatedStep$.pipe(first()));
        const stepNumber = steps.findIndex(x => x.workflowStepType === activatedStep.workflowStepType);
        await this.changeStep(steps[stepNumber + 1].workflowStepType);
    }

    public async goToPreviousStep(): Promise<void> {
        const steps = await lastValueFrom(this.steps$.pipe(first()));
        const activatedStep = await lastValueFrom(this.activatedStep$.pipe(first()));
        const stepNumber = steps.findIndex(x => x.workflowStepType === activatedStep.workflowStepType);
        await this.changeStep(steps[stepNumber - 1].workflowStepType);
    }

    public async initializeWorkflow(origin?: WorkflowOrigin, desiredInitialStep?: WorkflowStepType): Promise<void> {
        const workflowType = this._getWorkflowType(this._router.url, origin);
        if (!workflowType) {
            console.error('Error. Unable to set the workflow type !');
            return;
        }

        const jumpedFromCompanySearch = !!localStorage.getItem(StorageConstants.JUMPED_FROM_COMPANY_SEARCH);

        this._workflowDataInitializationService.initWorkflowData(
            workflowType,
            origin ?? WorkflowOrigin.WaremaPlusDefault,
            jumpedFromCompanySearch,
            desiredInitialStep,
        );

        if (!jumpedFromCompanySearch) {
            await this._navigateToCurrentStep();
        }
    }

    private async _navigateToCurrentStep(): Promise<void> {
        const activatedStep = await lastValueFrom(this.activatedStep$.pipe(first(x => !!x)));
        await this._router.navigate([activatedStep?.url], { queryParamsHandling: 'merge', relativeTo: this._route });
    }

    private _getWorkflowType(url: string, origin?: WorkflowOrigin): WorkflowType | null {
        if (url.includes('?')) {
            url = url.split('?')[0];
        }
        if (origin === WorkflowOrigin.Configurator) {
            if (url.match(/^\/project\/(.+?)\/request\/appointment/g)) {
                return WorkflowType.Appointment;
            } else if (url.match(/^\/project\/(.+?)\/request\/offer/g)) {
                return WorkflowType.Offer;
            }
        }

        // Warema plus
        if (url.startsWith('/company-search-workflow')) {
            return WorkflowType.CompanySearch;
        } else if (url.startsWith('/appointment-workflow')) {
            return WorkflowType.Appointment;
        } else if (url.startsWith('/offer-workflow')) {
            return WorkflowType.Offer;
        }

        // SEM
        if (url.startsWith('/new-consultation') || url.startsWith('/consultation')) {
            return WorkflowType.CompanySearch;
        }

        return null;
    }
}
